import { ISelectOption } from '@private/components';

import { TRANSACTIONS_TYPE } from 'api/transactions/constants';
import { UTC_TIME_ZONE } from 'components/Form/FormikIanaTimezoneSelect/constants';
import { METADATA_KEY_VALUES } from 'routes/pages/AuthorizedStack/TrafficManagement/RoutingRuleset/Ruleset/Form/utils/constants';
import {
  IMetadata,
  IStats,
} from 'routes/pages/AuthorizedStack/TrafficManagement/RoutingRuleset/Ruleset/Form/utils/types';
import { INullable, TObject } from 'utils/types';

import { ROUTING_RULESET_OPERATIONS } from './constants';
import {
  CASHIER_FLAG_MODE,
  CASHIER_PM_MODE,
  CROSS_STATS_MODE,
  IBaseRulesetData,
  IBulkCopyCashierConditional,
  IBulkCopyCopySettings,
  IBulkCopyRulesetsResult,
  IBulkCopyTerminalLinkAlongSettings,
  IMergeOperationDetail,
  IMergeOperationResult,
  IPeriodsByDays,
  IRevertApplyResult,
  IRevertPreviewEntry,
  IRevertPreviewResult,
  IRoutingRuleset,
  IRoutingRulesetData,
  IRoutingRulesetOperationsData,
  IRoutingRulesetOperators,
  IRulesetBase,
  IRulesetsPaymentMethods,
  ISingleRoutingRulesetData,
  ISingleRuleset,
  ISingleRulesetData,
  TERMINAL_LINK_MODE,
  TRulesetsPaymentMethodsData,
} from './types';

const normalizePaymentProviderCurrency = (data: TObject) => {
  if (!data || !data?.currency) {
    return null;
  }
  return {
    currency: (data?.currency as string) || null,
    exchangeConfirmation: !!data?.exchangeConfirmation,
    exchangeRateAdjustment: (data?.exchangeRateAdjustment as number) || 0,
    useCurrencyConversion: !!data?.useCurrencyConversion,
  };
};

const normalizeCashierPaymentMethod = (data?: TObject) => {
  if (!data) {
    return null;
  }
  return {
    id: (data?.id as number) || 0,
    logoData: (data?.logoData as string) || '',
    merchantId: (data?.merchantId as number) || 0,
    merchantName: (data?.merchantName as string) || '',
    merchantTerminalId: (data?.merchantTerminalId as number) || 0,
    merchantTerminalName: (data?.merchantTerminalName as string) || '',
    name: (data?.name as string) || '',
    paymentMethod: (data?.paymentMethod as string) || '',
  };
};

const normalizeEntity = (data: TObject) => {
  if (!data) {
    return null;
  }
  return {
    id: (data?.id as number) || 0,
    name: (data?.name as string) || '',
  };
};

const normalizeTerminalLinkMerchantTerminal = (
  data: IBaseRulesetData['terminalLink']['merchantTerminal'],
) => {
  if (!data) {
    return null;
  }
  return {
    apiAccessMode: (data?.apiAccessMode as string) || '',
    created: (data?.created as string) || '',
    email: (data?.email as string) || '',
    enableCascading: !!data?.enableCascading,
    enableCashier: !!data?.enableCashier,
    hqLegalEntity: normalizeEntity(data?.hqLegalEntity),
    id: (data?.id as number) || 0,
    merchant: normalizeEntity(data?.merchant),
    name: (data?.name as string) || '',
    platform: (data?.platform as string) || '',
    preMemorisedRouting: !!data?.preMemorisedRouting,
    shopUrl: (data?.shopUrl as string) || '',
    useCardToken: !!data?.useCardToken,
    useRoutingPriority: !!data?.useRoutingPriority,
  };
};

const normalizeTerminalLinkBankTerminal = (
  data: IBaseRulesetData['terminalLink']['bankTerminal'],
) => {
  return {
    bank: {
      id: (data?.bank?.id as number) || 0,
      name: (data?.bank?.name as string) || '',
      paymentMethod: (data?.bank?.paymentMethod as string) || '',
    },
    id: (data?.id as number) || 0,
    name: (data?.name as string) || '',
  };
};

const normalizeTimerConfig = (data: TObject) => {
  if (!data) {
    return null;
  }
  return {
    failRecoveryPeriod: (data?.failRecoveryPeriod as number) ?? null,
    failRetryCount: (data?.failRetryCount as number) ?? null,
    failTimerEnabled: !!data?.failTimerEnabled,
    successRetryCount: (data?.successRetryCount as number) ?? null,
    successTimerEnabled: !!data?.successTimerEnabled,
    successValidityPeriod: (data?.successValidityPeriod as number) ?? null,
  };
};

const normalizeTerminalLinkParameters = (data: TObject[]) => {
  if (!data?.length) {
    return null;
  }
  return data?.map((item: TObject) => ({
    dataType: (item?.id as string) || '',
    id: (item?.id as number) || 0,
    name: (item?.id as string) || '',
    value: (item?.id as string) || '',
  }));
};

const normalizeStats = (data: TObject): IStats => {
  return {
    ccDpAmount: (data?.ccDpAmount as number) ?? null,
    ccDpCount: (data?.ccDpCount as number) ?? null,
    ccWdAmount: (data?.ccWdAmount as number) ?? null,
    ccWdCount: (data?.ccWdCount as number) ?? null,
    dpAmount: (data?.dpAmount as number) ?? null,
    dpCount: (data?.dpCount as number) ?? null,
    uccDpAmount: (data?.uccDpAmount as number) ?? null,
    uccDpCount: (data?.uccDpCount as number) ?? null,
    uccWdAmount: (data?.uccWdAmount as number) ?? null,
    uccWdCount: (data?.uccWdCount as number) ?? null,
    wdAmount: (data?.wdAmount as number) ?? null,
    wdCount: (data?.wdCount as number) ?? null,
  };
};

const normalizeCrossStats = (
  data: ISingleRulesetData['crossStats'],
): INullable<ISingleRuleset['crossStats']> => {
  if (!data) {
    return null;
  }
  return {
    cumulativeStats: {
      enabled: !!data?.cumulativeStats?.enabled,
      ...normalizeStats(data?.cumulativeStats),
    },
    id: (data?.id as number) || 0,
    leaderStats: {
      enabled: !!data?.leaderStats?.enabled,
      minDaysFromFTD: (data?.leaderStats?.minDaysFromFTD as number) ?? null,
      minDaysFromRegistration:
        (data?.leaderStats?.minDaysFromRegistration as number) ?? null,
      registeredBefore: (data?.leaderStats?.registeredBefore as string) || null,
      ...normalizeStats(data?.leaderStats),
    },
    merchantGroup: {
      created: (data?.merchantGroup?.created as string) || '',
      email: !!data?.merchantGroup?.email,
      firstName: !!data?.merchantGroup?.firstName,
      id: (data?.merchantGroup?.id as number) || 0,
      lastName: !!data?.merchantGroup?.lastName,
      merchant: {
        id: (data?.merchantGroup?.merchant?.id as number) || 0,
        name: !!data?.merchantGroup?.merchant?.id,
      },
      merchantTerminalIds: (data?.merchantGroup?.merchantTerminalIds as number[]) || [],
      merchantTerminalNames:
        (data?.merchantGroup?.merchantTerminalNames as string[]) || [],
      name: (data?.merchantGroup?.name as string) || '',
      phone: !!data?.merchantGroup?.phone,
    },
  };
};

const baseRulesetNormalization = (data: IBaseRulesetData): IRulesetBase => {
  return {
    cashierPaymentMethod: normalizeCashierPaymentMethod(data?.cashierPaymentMethod),
    closedLoopEnabled: !!data?.closedLoopEnabled,
    closedLoopTerminalLinks: (data?.closedLoopTerminalLinks as number[]) || [],
    commissionMin: (data?.commissionMin as number) ?? null,
    commissionOverride: !!data?.commissionOverride,
    commissionPct: (data?.commissionPct as number) ?? null,
    crossStats: normalizeCrossStats(data?.crossStats),
    enabled: !!data?.enabled,
    id: (data?.id as number) || 0,
    merchantTerminal: {
      apiAccessMode: (data?.merchantTerminal?.apiAccessMode as string) || null,
      changePaymentMethod: !!data?.merchantTerminal?.changePaymentMethod,
      created: (data?.merchantTerminal?.created as string) || null,
      email: (data?.merchantTerminal?.email as string) || null,
      enableCascading: !!data?.merchantTerminal?.enableCascading,
      enableCashier: !!data?.merchantTerminal?.enableCashier,
      hqLegalEntity: normalizeEntity(data?.merchantTerminal?.hqLegalEntity),
      id: (data?.merchantTerminal?.id as number) || 0,
      merchant: normalizeEntity(data?.merchantTerminal?.merchant),
      name: (data?.merchantTerminal?.name as string) || '',
      platform: (data?.merchantTerminal?.platform as string) || null,
      preMemorisedRouting: !!data?.merchantTerminal?.preMemorisedRouting,
      shopUrl: (data?.merchantTerminal?.shopUrl as string) || null,
      useCardToken: !!data?.merchantTerminal?.useCardToken,
      useRoutingPriority: !!data?.merchantTerminal?.useRoutingPriority,
    },
    name: (data?.name as string) || '',
    paymentMethod: (data?.paymentMethod as string) || null,
    priority: (data?.priority as number) ?? null,
    ratio: (data?.ratio as number) ?? null,
    terminalLink: {
      bankTerminal: normalizeTerminalLinkBankTerminal(data?.terminalLink?.bankTerminal),
      description: (data?.terminalLink?.description as string) || null,
      enabled: !!data?.terminalLink?.enabled,
      exitIFrame: !!data?.terminalLink?.exitIFrame,
      id: (data?.terminalLink?.id as number) || 0,
      limitsEnabled: !!data?.terminalLink?.limitsEnabled,
      merchantTerminal: normalizeTerminalLinkMerchantTerminal(
        data?.terminalLink?.merchantTerminal,
      ),
      motoEnabled: !!data?.terminalLink?.motoEnabled,
      parameters: normalizeTerminalLinkParameters(data?.terminalLink?.parameters),
      recurringEnabled: !!data?.terminalLink?.recurringEnabled,
      subPaymentMethod: (data?.terminalLink?.subPaymentMethod as string) || null,
      threeDSEnabled: !!data?.terminalLink?.threeDSEnabled,
      trusted: !!data?.terminalLink?.trusted,
      webhooksEnabled: !!data?.terminalLink?.webhooksEnabled,
    },
    timerConfig: normalizeTimerConfig(data?.timerConfig),
  };
};

const normalizePeriodsByDays = (data: Record<string, TObject>): IPeriodsByDays => {
  if (!data || !Object.keys(data || {}).length) {
    return null;
  }
  return Object.fromEntries(
    Object.entries(data).map(([key, value]) => [
      key,
      value
        ? { from: (value?.from as string) || null, to: (value?.to as string) || null }
        : null,
    ]),
  );
};

const normalizeRoutingSchema = (data: TObject) => {
  return {
    active: !!data?.active,
    id: (data?.id as number) || null,
    name: (data?.name as string) || null,
  };
};

export const normalizeRoutingRuleset = (data: IRoutingRulesetData): IRoutingRuleset => {
  return {
    ...baseRulesetNormalization(data),
    billingInfo: !!data?.billingInfo,
    binIssuingOrgOperation: (data?.binIssuingOrgOperation as string) || '',
    binIssuingOrgValue: Array.isArray(data?.binIssuingOrgValue)
      ? data.binIssuingOrgValue
      : (data?.binIssuingOrgValue || '').split(':').filter(Boolean),
    binRangeOperation: (data?.binRangeOperation as string) || '',
    binRangeValue: (data?.binRangeValue as string) || '',
    cardNetwork: (data?.cardNetwork as string) || '',
    cardType: data?.cardType
      ? (((data?.cardType as string) || '')?.split(':') as string[])
      : [],
    compositeAmount: (data?.compositeAmount as string) || '',
    compositeBinCountry: (data?.compositeBinCountry as string) || '',
    compositeBinCountryGroup: (data?.compositeBinCountryGroup as string) || '',
    compositeBinGroup: (data?.compositeBinGroup as string) || '',
    compositeCustomerCountry: (data?.compositeCustomerCountry as string) || '',
    compositeCustomerCountryGroup: (data?.compositeCustomerCountryGroup as string) || '',
    compositeCustomerIp: (data?.compositeCustomerIp as string) || '',
    compositeTxAmount: (data?.compositeTxAmount as string) || '',
    customerCcDpAmount: data?.customerCcDpAmount as number,
    customerCcDpCount: data?.customerCcDpCount as number,
    customerCcWdAmount: data?.customerCcWdAmount as number,
    customerCcWdCount: data?.customerCcWdCount as number,
    customerDaysFromFtd: data?.customerDaysFromFtd as number,
    customerDaysFromReg: data?.customerDaysFromReg as number,
    customerDpAmount: data?.customerDpAmount as number,
    customerDpCount: data?.customerDpCount as number,
    customerMaxCcDpAmount: data?.customerMaxCcDpAmount as number,
    customerMaxCcDpCount: data?.customerMaxCcDpCount as number,
    customerMaxCcWdAmount: data?.customerMaxCcWdAmount as number,
    customerMaxCcWdCount: data?.customerMaxCcWdCount as number,
    customerMaxDpAmount: data?.customerMaxDpAmount as number,
    customerMaxDpCount: data?.customerMaxDpCount as number,
    customerMaxUccDpAmount: data?.customerMaxUccDpAmount as number,
    customerMaxUccDpCount: data?.customerMaxUccDpCount as number,
    customerMaxUccWdAmount: data?.customerMaxUccWdAmount as number,
    customerMaxUccWdCount: data?.customerMaxUccWdCount as number,
    customerMaxWdAmount: data?.customerMaxWdAmount as number,
    customerMaxWdCount: data?.customerMaxWdCount as number,
    customerRegBeforeDate: (data?.customerRegBeforeDate as string) || '',
    customerUccDpAmount: data?.customerUccDpAmount as number,
    customerUccDpCount: data?.customerUccDpCount as number,
    customerUccWdAmount: data?.customerUccWdAmount as number,
    customerUccWdCount: data?.customerUccWdCount as number,
    customerWdAmount: data?.customerWdAmount as number,
    customerWdCount: data?.customerWdCount as number,
    exchangeConfirmation: !!data?.exchangeConfirmation,
    exchangeRateAdjustment: data?.exchangeRateAdjustment as number,
    excludedFromCascading: !!data?.excludedFromCascading,
    metadata: {
      equalsValues: data?.metadata?.equalsValues as string,
      exist: data?.metadata?.exist as string,
      inValues: data?.metadata?.inValues as string,
      notEqualsValues: data?.metadata?.notEqualsValues as string,
      notExist: data?.metadata?.notExist as string,
      notInValues: data?.metadata?.notInValues as string,
    },
    paymentProviderCurrency: (data?.paymentProviderCurrency as string) || '',
    phonePrefixOperation: (data?.phonePrefixOperation as string) || '',
    phonePrefixValue: (data?.phonePrefixValue as string) || '',
    routingSchema: normalizeRoutingSchema(data.routingSchema),
    schedule: !!data?.schedule,
    transactionType: (data?.transactionType as TRANSACTIONS_TYPE) || '',
    txCurrency: (data?.txCurrency as string) || '',
    useCurrencyConversion: !!data?.useCurrencyConversion,
  };
};

export const normalizeOperators = (
  data: IRoutingRulesetOperationsData,
): IRoutingRulesetOperators => {
  return {
    bin: {
      binGroup: (data.bin?.binGroup as string[]) || [],
      country: (data.bin?.country as string[]) || [],
      countryGroup: (data.bin?.countryGroup as string[]) || [],
      issuingOrg: (data.bin?.issuingOrg as string[]) || [],
      range: (data.bin?.range as string[]) || [],
    },
    customer: {
      country: (data.customer?.country as string[]) || [],
      countryGroup: (data.customer?.countryGroup as string[]) || [],
      ip: (data.customer?.ip as string[]) || [],
      // Phone Prefix operation is fixed to IN / NOT IN by the contract. Fall back to
      // that pair when the operators endpoint doesn't supply it, so the selector is
      // never empty regardless of the BE build.
      phonePrefix: (data.customer?.phonePrefix as string[])?.length
        ? (data.customer?.phonePrefix as string[])
        : [ROUTING_RULESET_OPERATIONS.IN, ROUTING_RULESET_OPERATIONS.NOT_IN],
    },
    transaction: {
      amount: (data.transaction?.amount as string[]) || [],
      metadata: (data.transaction?.metadata as string[]) || [],
      txAmount: (data.transaction?.txAmount as string[]) || [],
    },
  };
};

const normalizeAmount = (data?: TObject) => {
  if (!data || (Object.keys(data).length === 1 && data?.currencyMerchantTerminal)) {
    return null;
  }
  return {
    maxValue: (data?.maxValue as string) ?? null,
    minValue: (data?.minValue as string) ?? null,
    operation: (data?.operation as string) || null,
  };
};

const normalizeMetaData = (data?: TObject): INullable<IMetadata[]> => {
  if (!data) {
    return null;
  }
  const result: INullable<IMetadata[]> = [];

  Object.entries(data).forEach(([operation, value]) => {
    const isExistsOperators =
      operation === METADATA_KEY_VALUES.EXIST ||
      operation === METADATA_KEY_VALUES.NOT_EXIST;
    const isEqualsOperators =
      operation === METADATA_KEY_VALUES.EQUALS ||
      operation === METADATA_KEY_VALUES.NOT_EQUALS;
    const isInOperators =
      operation === METADATA_KEY_VALUES.IN || operation === METADATA_KEY_VALUES.NOT_IN;

    (value as string).split(',').forEach((item) => {
      if (isExistsOperators) {
        result.push({
          key: item,
          operation,
          useTemplate: false,
          value: null,
        });
      } else {
        // Split on the first colon only so values containing colons
        // (e.g. `key:https://example.com`) are preserved verbatim.
        const separatorIndex = item.indexOf(':');
        const key = separatorIndex === -1 ? item : item.slice(0, separatorIndex);
        const values = separatorIndex === -1 ? '' : item.slice(separatorIndex + 1);

        if (isEqualsOperators) {
          result.push({
            key,
            operation,
            useTemplate: false,
            value: values,
          });
        } else if (isInOperators) {
          const res = values.split('|');
          result.push({
            key,
            operation,
            useTemplate: false,
            value: res,
          });
        }
      }
    });
  });

  return result;
};

const normalizeTransaction = (data: ISingleRoutingRulesetData['transaction']) => {
  return {
    amount: normalizeAmount(data?.amount),
    excludedFromCascading: !!data?.excludedFromCascading,
    metadata: normalizeMetaData(data?.metadata),
    txAmount: normalizeAmount(data?.txAmount),
    txCcy: (data?.txCcy as string[]) || null,
    type: (data?.type as string) || null,
  };
};

const normalizeBinRange = (data: TObject) => {
  if (!data) {
    return null;
  }

  return {
    operation: (data?.operation as string) || '',
    value: (data?.value as string) || '',
  };
};

const normalizePhonePrefix = (data: TObject) => {
  if (!data) {
    return null;
  }

  return {
    operation: (data?.operation as string) || '',
    value: (data?.value as string) || '',
  };
};

const normalizeBinCountry = (data: TObject) => {
  if (!data) {
    return null;
  }
  return {
    countryCode: (data?.countryCode as string) || '',
    operation: (data?.operation as string) || '',
  };
};

const normalizeBinCountryGroup = (data: TObject) => {
  if (!data) {
    return null;
  }
  return {
    groupId: (data?.groupId as number) || 0,
    operation: (data?.operation as string) || '',
  };
};

const normalizeIssuingOrg = (data: TObject) => {
  if (!data) {
    return null;
  }

  const valuesList = (data?.valuesList as INullable<string[]>) || [];

  return {
    operation: (data?.operation as string) || null,
    valuesList: valuesList.length ? valuesList : null,
  };
};

const normalizeBin = (data: ISingleRoutingRulesetData['bin']) => {
  if (!data) {
    return null;
  }
  return {
    billingInfo: !!data?.billingInfo,
    binGroup: normalizeBinCountryGroup(data?.binGroup),
    cardBrands: (data?.cardBrands as string[]) || null,
    cardType: data?.cardType
      ? (((data?.cardType as string) || '')?.split(':') as string[])
      : [],
    country: normalizeBinCountry(data?.country),
    countryGroup: normalizeBinCountryGroup(data?.countryGroup),
    issuingOrg: normalizeIssuingOrg(data?.issuingOrg),
    range: normalizeBinRange(data?.range),
  };
};

const normalizeCustomerIp = (data: TObject) => {
  if (!data) {
    return null;
  }

  return {
    countryCodes: (data?.countryCodes as string[]) || [],
    operation: (data?.operation as string) || '',
  };
};

const normalizeCustomerCountryGroup = (data: TObject) => {
  if (!data) {
    return null;
  }

  return {
    groupId: (data?.groupId as number) || 0,
    groupName: (data?.groupName as string) || '',
    operation: (data?.operation as string) || '',
  };
};

const normalizeCustomerCountry = (data: TObject) => {
  if (!data) {
    return null;
  }

  return {
    countryCode: (data?.countryCode as string) || '',
    operation: (data?.operation as string) || '',
  };
};

const normalizeCustomer = (data: ISingleRoutingRulesetData['customer']) => {
  if (!data) {
    return null;
  }
  return {
    country: normalizeCustomerCountry(data?.country),
    countryGroup: normalizeCustomerCountryGroup(data?.countryGroup),
    daysFromFtd: (data?.daysFromFtd as number) ?? null,
    daysFromReg: (data?.daysFromReg as number) ?? null,
    ip: normalizeCustomerIp(data?.ip),
    phonePrefix: normalizePhonePrefix(data?.phonePrefix),
    regBeforeDate: (data?.regBeforeDate as string) || null,
  };
};

export const normalizeSingleRoutingRuleset = (
  data: ISingleRulesetData,
): ISingleRuleset => {
  return {
    ...baseRulesetNormalization(data),
    bin: normalizeBin(data?.bin),
    customer: normalizeCustomer(data?.customer),
    maxStats: data?.maxStats ? normalizeStats(data?.maxStats) : null,
    paymentProviderCurrency: normalizePaymentProviderCurrency(
      data?.paymentProviderCurrency,
    ),
    routingSchema: normalizeRoutingSchema(data.routingSchema),
    schedule: {
      enabled: !!data?.schedule?.enabled,
      periodsByDays: normalizePeriodsByDays(data?.schedule?.periodsByDays),
      timeZone: data?.schedule?.timeZone || UTC_TIME_ZONE,
    },
    stats: data?.stats ? normalizeStats(data?.stats) : null,
    transaction: normalizeTransaction(data?.transaction),
  };
};

export const normalizedRulesetsPaymentMethods = (
  data: TRulesetsPaymentMethodsData,
): IRulesetsPaymentMethods => {
  const entries = Object.entries(data?.paymentMethods);

  const paymentMethods: ISelectOption[] = data?.paymentMethods
    ? entries.map(([code, name]) => {
        return {
          label: name as string,
          value: code,
        };
      })
    : [];

  return {
    merchantTerminalId: (data?.merchantTerminalId as number) || 0,
    paymentMethods,
  };
};

const normalizeBulkCopyTerminalLinkAlongSettings = (
  data?: TObject,
): IBulkCopyTerminalLinkAlongSettings | undefined => {
  if (!data) {
    return undefined;
  }
  return {
    withGroupLimits: !!data?.withGroupLimits,
    withOverriddenParameters: !!data?.withOverriddenParameters,
    withRequiredParameters: !!data?.withRequiredParameters,
    withTerminalLinkLimits: !!data?.withTerminalLinkLimits,
  };
};

const normalizeBulkCopyCashierConditional = (
  data?: TObject,
): IBulkCopyCashierConditional | undefined => {
  if (!data) {
    return undefined;
  }
  return {
    cashierLimitsMode:
      (data?.cashierLimitsMode as CASHIER_FLAG_MODE) || CASHIER_FLAG_MODE.WITHOUT,
    cashierSettingsMode:
      (data?.cashierSettingsMode as CASHIER_FLAG_MODE) || CASHIER_FLAG_MODE.WITHOUT,
  };
};

const normalizeBulkCopyCopySettings = (data?: TObject): IBulkCopyCopySettings => {
  return {
    cashierConditionalSettings: normalizeBulkCopyCashierConditional(
      data?.cashierConditionalSettings as TObject,
    ),
    cashierPaymentMethodMode:
      (data?.cashierPaymentMethodMode as CASHIER_PM_MODE) || CASHIER_PM_MODE.WITHOUT,
    crossStatsMode: (data?.crossStatsMode as CROSS_STATS_MODE) || CROSS_STATS_MODE.NONE,
    terminalLinkAlongWithSettings: normalizeBulkCopyTerminalLinkAlongSettings(
      data?.terminalLinkAlongWithSettings as TObject,
    ),
    terminalLinkMode:
      (data?.terminalLinkMode as TERMINAL_LINK_MODE) || TERMINAL_LINK_MODE.WITHOUT,
  };
};

export const normalizeBulkCopyRulesetsResult = (
  data?: TObject,
): IBulkCopyRulesetsResult => {
  return {
    copiedRulesetCount: (data?.copiedRulesetCount as number) || 0,
    copiedRulesetIdsByTargetMerchant:
      (data?.copiedRulesetIdsByTargetMerchant as Record<string, number[]>) || {},
    copySettings: normalizeBulkCopyCopySettings(data?.copySettings as TObject),
    targetMerchantCount: (data?.targetMerchantCount as number) || 0,
  };
};

export const normalizeMergeOperation = (data?: TObject): IMergeOperationResult => ({
  created: (data?.created as number) || 0,
  createdAt: (data?.createdAt as string) || '',
  finishedAt: (data?.finishedAt as string) || null,
  keycloakId: (data?.keycloakId as string) || '',
  login: (data?.login as string) || '',
  noOp: (data?.noOp as number) || 0,
  operationId: (data?.operationId as number) || 0,
  searchQuery: (data?.searchQuery as string) || '',
  skippedMismatch: (data?.skippedMismatch as number) || 0,
  status: (data?.status as string) || '',
  totalMatched: (data?.totalMatched as number) || 0,
  updated: (data?.updated as number) || 0,
  validationErrors: (data?.validationErrors as number) || 0,
});

export const normalizeMergeOperationDetail = (data: TObject): IMergeOperationDetail => ({
  detail: (data?.detail as string) || '',
  key: (data?.key as string) || '',
  outcome: (data?.outcome as string) || '',
  rulesetId: (data?.rulesetId as number) || 0,
  rulesetName: (data?.rulesetName as string) || '',
  section: (data?.section as string) || '',
  skipReason: (data?.skipReason as string) || null,
});

export const normalizeRevertApplyResult = (data?: TObject): IRevertApplyResult => ({
  createdAt: (data?.createdAt as string) || '',
  errorDetail: (data?.errorDetail as string) || null,
  finishedAt: (data?.finishedAt as string) || null,
  keycloakId: (data?.keycloakId as string) || '',
  login: (data?.login as string) || '',
  operationId: (data?.operationId as number) || 0,
  reverted: (data?.reverted as number) || 0,
  searchQuery: (data?.searchQuery as string) || '',
  status: (data?.status as string) || '',
  targetTime: (data?.targetTime as string) || '',
  totalPreviewed: (data?.totalPreviewed as number) || 0,
});

export const normalizeRevertPreviewEntry = (data?: TObject): IRevertPreviewEntry => ({
  diff: ((data?.diff as TObject[]) || []).map((diffItem) => ({
    currentValue: (diffItem?.currentValue as string) ?? null,
    field: (diffItem?.field as string) || '',
    newValue: (diffItem?.newValue as string) ?? null,
  })),
  rulesetId: (data?.rulesetId as number) || 0,
  rulesetName: (data?.rulesetName as string) || '',
});

export const normalizeRevertPreviewResult = (data?: TObject): IRevertPreviewResult => ({
  entries: ((data?.entries as TObject[]) || []).map(normalizeRevertPreviewEntry),
  historyStartAt: (data?.historyStartAt as string) || '',
  previewToken: (data?.previewToken as string) || '',
  targetTime: (data?.targetTime as string) || '',
});
